import { displayWorkSummary } from '../../supabase/functions/_shared/workSummary'
import { activityLabel } from '../../supabase/functions/_shared/activityDetails'
import { ActivitySummaryStatus } from './ActivitySummaryStatus'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CircleUserRound } from 'lucide-react'
import { supabase } from '../lib/supabase'
import type { WorkActivity } from '../lib/agentActivity'
import { formatModelLabelShort } from '../lib/modelLabel'
import { CardContent, CardHeader, CardTitle, CardDescription } from './ui/card'
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar'
import { EmptyState } from './ui/empty-state'
import { Skeleton } from './ui/skeleton'
import { UsageCostText } from './UsageCost'
import { rangeLabelAsOf } from '../lib/dayLabel'
import {
  emptyTotals,
  fetchTeamAgentUsageDaily,
  addToTotals,
  sumByAgent,
  type AgentUsageDailyRow,
  type UsageTotals,
} from '../lib/agentUsage'

// Recent recorded connector work is separate from the latest human standup.
// The preview RPC and underlying tables enforce the current viewer's access.

interface PeopleAndAgentsPreviewProps {
  teamId: string
  range: { from: string; to: string } // YYYY-MM-DD
  userIds?: string[]
}

interface Entry {
  userId: string
  name: string
  avatarUrl: string | null
  note: string | null
  standupTime: string | null
  activities: (WorkActivity & { label: string })[]
  activityCount: number
  latestAt: string
  usage: UsageTotals
}

const tokenFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
})

const formatTime = (iso: string | null): string | null => (iso ? timeFormatter.format(new Date(iso)) : null)

const PeopleAndAgentsPreview: React.FC<PeopleAndAgentsPreviewProps> = ({ teamId, range, userIds }) => {
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      // Empty (but non-null) userIds means "pod has no members" -- handled
      // explicitly rather than sent as `.in('id', [])`.
      if (userIds && userIds.length === 0) {
        setEntries([])
        setLoading(false)
        return
      }

      let usersQuery = supabase.from('users').select('id, name, email, avatar_url').eq('team_id', teamId)
      if (userIds) usersQuery = usersQuery.in('id', userIds)

      const [usersResult, standupsResult, agentsResult, activityResult, usageResult] = await Promise.all([
        usersQuery,
        supabase
          .from('standup_responses')
          .select('user_id, questions, created_at, date')
          .eq('team_id', teamId)
          .gte('date', range.from)
          .lte('date', range.to),
        (supabase.from as any)('agents_team_visible')
          .select('id, user_id, name, client_id, client_name')
          .eq('team_id', teamId),
        (supabase.rpc as any)('get_dashboard_work_preview', {
          p_team_id: teamId, p_from: range.from, p_to: range.to, p_user_ids: userIds ?? null,
        }),
        fetchTeamAgentUsageDaily(teamId, range.from, range.to).then(
          (rows) => ({ rows, error: null as unknown }),
          (error: unknown) => ({ rows: [] as AgentUsageDailyRow[], error })
        ),
      ])

      if (cancelled) return

      if (
        usersResult.error ||
        standupsResult.error ||
        agentsResult.error ||
        activityResult.error ||
        usageResult.error
      ) {
        console.error(
          'Failed to load people + agents preview:',
          usersResult.error ?? standupsResult.error ?? agentsResult.error ?? activityResult.error ?? usageResult.error
        )
        setError('Could not load activity for this window.')
        setLoading(false)
        return
      }

      // Keyed by user_id, keeping only the highest `date` seen per person —
      // same "collapse to most recent in range" choice NeedsAttentionCard
      // makes, so a person's OLDER update in the range never wins over a
      // newer one.
      const standupByUserId = new Map<
        string,
        { questions: { yesterday?: string; today?: string } | null; created_at: string; date: string }
      >()
      for (const s of (standupsResult.data ?? []) as {
        user_id: string | null
        questions: { yesterday?: string; today?: string } | null
        created_at: string
        date: string
      }[]) {
        if (!s.user_id) continue
        const existing = standupByUserId.get(s.user_id)
        if (!existing || s.date > existing.date) {
          standupByUserId.set(s.user_id, { questions: s.questions, created_at: s.created_at, date: s.date })
        }
      }

      const agents = (agentsResult.data ?? []) as { id: string; user_id: string; name: string; client_name: string | null }[]
      const agentById = new Map(agents.map(agent => [agent.id, agent]))
      const activitiesByUser = new Map<string, (WorkActivity & { label: string })[]>()
      for (const activity of (activityResult.data ?? []) as (WorkActivity & { user_id: string })[]) {
        const agent = agentById.get(activity.agent_id)
        if (!agent || agent.user_id !== activity.user_id) continue
        const list = activitiesByUser.get(activity.user_id) ?? []
        list.push({ ...activity, label: agent.client_name || agent.name })
        activitiesByUser.set(activity.user_id, list)
      }
      const usageByAgent = sumByAgent(usageResult.rows)
      const usageByUser = new Map<string, UsageTotals>()
      for (const agent of agents) {
        const totals = usageByAgent.get(agent.id)
        if (!totals) continue
        const userTotals = usageByUser.get(agent.user_id) ?? emptyTotals()
        addToTotals(userTotals, totals)
        usageByUser.set(agent.user_id, userTotals)
      }

      const users = (usersResult.data ?? []) as {
        id: string
        name: string | null
        email: string | null
        avatar_url: string | null
      }[]

      const built: Entry[] = users
        .map((u) => {
          const standup = standupByUserId.get(u.id)
          const activities = (activitiesByUser.get(u.id) ?? []).sort((a, b) => b.created_at.localeCompare(a.created_at))
          const usage = usageByUser.get(u.id) ?? emptyTotals()
          return {
            userId: u.id,
            name: u.name || u.email?.split('@')[0] || 'Unknown user',
            avatarUrl: u.avatar_url,
            note: standup?.questions?.today ?? null,
            standupTime: formatTime(standup?.created_at ?? null),
            activities: activities.slice(0, 3),
            activityCount: Number(activities[0]?.total_count ?? activities.length),
            latestAt: [standup?.created_at, activities[0]?.created_at].filter(Boolean).sort().slice(-1)[0] ?? '',
            usage,
          }
        })
        .filter((entry) => entry.note || entry.activities.length || entry.usage.eventCount > 0)
        .sort((a, b) => b.latestAt.localeCompare(a.latestAt) || a.name.localeCompare(b.name))
        .slice(0, 6)

      setEntries(built)
      setLoading(false)
    }

    load().catch(() => {
      if (!cancelled) {
        setError('Could not load activity for this window.')
        setLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [teamId, range.from, range.to, userIds?.join(",")])

  return (
    <div className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="text-base">People and their connectors</CardTitle>
        <CardDescription>
          Latest human update and up to three recent recorded work summaries per person {rangeLabelAsOf(range.from, range.to)}.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col p-0">
        {loading ? (
          // Mirrors a loaded row: person avatar + name/note lines, with an
          // indented "elbow"-connected agent sub-row underneath for roughly
          // half the rows (not every person has an agent).
          <div className="divide-y divide-border border-y border-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="px-6 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <Skeleton className="mt-0.5 h-10 w-10 shrink-0 rounded-full" />
                    <div className="min-w-0 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                  <Skeleton className="h-3 w-12 shrink-0" />
                </div>
                {i < 2 && (
                  <div className="relative ml-5 mt-3 pl-6">
                    <div className="flex items-start gap-2">
                      <Skeleton className="mt-0.5 h-4 w-4 shrink-0 rounded-md" />
                      <div className="min-w-0 flex-1 space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-56" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="px-6 pb-6 text-sm text-destructive">{error}</p>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={CircleUserRound}
            message={`No activity ${rangeLabelAsOf(range.from, range.to)}.`}
          />
        ) : (
          <div className="divide-y divide-border border-y border-border">
            {entries.map((entry) => (
              <div key={entry.userId} className="px-6 py-4">
                <Link to={`/dashboard/team/${teamId}/standups/${entry.userId}`} className="block rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <Avatar className="mt-0.5 h-10 w-10 shrink-0">
                      {entry.avatarUrl && <AvatarImage src={entry.avatarUrl} alt="" />}
                      <AvatarFallback>{entry.name.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{entry.name}</p>
                      <p className="truncate text-sm text-muted-foreground">
                        {entry.note ?? `No human update ${rangeLabelAsOf(range.from, range.to)}`}
                      </p>
                    </div>
                  </div>
                  {entry.standupTime && (
                    <span className="shrink-0 text-sm text-muted-foreground">{entry.standupTime}</span>
                  )}
                </div>

                </Link>
                <div className="mt-3 space-y-3 border-l border-border pl-4 sm:ml-5">
                  {entry.activities.length > 0 ? (
                    <ul className="space-y-3" aria-label={`${entry.name}'s recent connector work`}>
                      {entry.activities.map(activity => (
                        <li key={`${activity.activity_kind}:${activity.id}`}>
                          <p className="break-words text-sm text-foreground">{displayWorkSummary(activity.task)}</p>
                          <p className="mt-1 break-words text-xs text-muted-foreground">
                            {activityLabel(activity.classification, activity.activity_kind)}
                            {' · '}{activity.label}{' · '}{activity.checkin_date}{' · '}{formatTime(activity.created_at)}
                            {activity.model ? ` · ${formatModelLabelShort(activity.model)}` : ''}
                          </p>
                          <ActivitySummaryStatus activity={activity} />
                        </li>
                      ))}
                    </ul>
                  ) : entry.usage.eventCount > 0 ? (
                    <p className="text-sm text-muted-foreground">Usage was recorded, but detailed activity is unavailable for this period.</p>
                  ) : null}
                  {entry.usage.eventCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {tokenFormatter.format(entry.usage.totalTokens)} tokens across visible connectors in this period
                      {' · '}<UsageCostText totals={entry.usage} />
                    </p>
                  )}
                  <Link
                    to={`/dashboard/team/${teamId}/standups/${entry.userId}?from=${range.from}&to=${range.to}`}
                    className="inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline"
                    aria-label={`View activity for ${entry.name}`}
                  >
                    View activity{entry.activityCount > 3 ? ` (${entry.activityCount} records)` : ''}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </div>
  )
}

export default PeopleAndAgentsPreview
